import * as React from 'react';
import { StyleSheet, css } from 'aphrodite/no-important';
import {
  ALIGNMENT,
  RATIO,
  TypographySize,
  SEMANTIC_TAG,
  ALIGNMENT_OPTIONS,
} from 'mm-theme-configuration/dist/consts';
import { CardOverlay } from '../../CardOverlay';
import { Image } from '../../image/Image';
import { CardCaptionContainer, CARD_CAPTION_CONTAINER_TYPES } from '../../CardCaptionContainer';
import { useTheme } from '../../../../theming/useTheme';
import { PlayIcon } from '../../PlayIcon';
import { WrapperForCard } from '../WrapperForCard';
import { Link } from '../../link/Link';
import { semanticCSS } from '../../../../typography/semanticTags';
import { createStyleForBoxShadow } from '../../../../theming/boxShadow';
import { CardComponentDataProps, CardComponentPropsFromLayout } from '../cards.utils';
import { DynamicSemanticTag } from '../../../../typography/semanticTags/DynamicSemanticTag';
import { createImageAspectRatios } from '../../image/image.utils';

const numberOfLines = 3;
export const componentConfigThemeKey = 'onTopCard';

export interface OnTopCardComponentThemeProps {
  cardTitleColorTypeNormal: string;
  cardTitleColorTypeHover: string;
  cardTitleColorTypeActive: string;
  borderRadius: number;
  boxShadowTypeNormal: string;
  boxShadowTypeActive: string;
  boxShadowTypeHover: string;
  borderWidthTypeNormal: number;
  borderWidthTypeHover: number;
  borderWidthTypeActive: number;
  borderColorTypeNormal: string;
  borderColorTypeHover: string;
  borderColorTypeActive: string;
  aspectRatioLarge: RATIO;
  aspectRatioMedium: RATIO;
  aspectRatioSmall: RATIO;
  overlayColor: string;
  titleFontSizeLarge: TypographySize;
  titleFontSizeMedium: TypographySize;
  titleFontSizeSmall: TypographySize;
  titleAlignment: ALIGNMENT;
  showDate: boolean;
  dateFontSizeLarge: TypographySize;
  dateFontSizeMedium: TypographySize;
  dateFontSizeSmall: TypographySize;
  dateColor: string;
  pipeColor: string;
  showAuthor: boolean;
  authorFontSizeLarge: TypographySize;
  authorFontSizeMedium: TypographySize;
  authorFontSizeSmall: TypographySize;
  authorColor: string;
  wrapperGapLarge: number;
  wrapperGapMedium: number;
  wrapperGapSmall: number;
  verticalGapLarge: number;
  verticalGapMedium: number;
  verticalGapSmall: number;
  horizontalGapLarge: number;
  horizontalGapMedium: number;
  horizontalGapSmall: number;
  titleSemanticTag: SEMANTIC_TAG;
  dateSemanticTag: SEMANTIC_TAG;
  authorSemanticTag: SEMANTIC_TAG;
}

type OnTopCardProps = CardComponentDataProps & CardComponentPropsFromLayout

const createCardStyles = (cardTextColorTypeNormal: string, cardTextColorTypeHover: string, cardTextColorTypeActive: string, borderRadius: number) => StyleSheet.create({
  card: {
    width: '100%',
    height: '100%',
    position: 'relative',
    textDecoration: 'none',
    color: cardTextColorTypeNormal,
    ':hover': {
      color: cardTextColorTypeHover,
    },
    ':active': {
      color: cardTextColorTypeActive,
    },
    display: 'block',
    '-webkit-mask-image': '-webkit-radial-gradient(white, black)',
    borderRadius: `${borderRadius}px`,
  },
});

const createTitleAlignmentStyle = (titleAlignment: ALIGNMENT) => {
  switch (titleAlignment) {
    case ALIGNMENT_OPTIONS.CENTER:
      return StyleSheet.create({
        style: {
          textAlign: 'center',
        },
      }).style;
    case ALIGNMENT_OPTIONS.START:
    default:
      return StyleSheet.create({
        style: {
          textAlign: 'left',
        },
      }).style;
  }
};

export const OnTopCard: React.FunctionComponent<OnTopCardProps> = props => {
  const {
    title,
    image,
    articleUrl,
    coverType,
    imageHeightCalculationMethods,
    imageWidths,
    lazyLoad = false,
  } = props;
  const {
    cardTitleColorTypeNormal,
    cardTitleColorTypeHover,
    cardTitleColorTypeActive,
    boxShadowTypeNormal,
    boxShadowTypeActive,
    boxShadowTypeHover,
    borderRadius,
    borderColorTypeActive,
    borderColorTypeHover,
    borderColorTypeNormal,
    borderWidthTypeActive,
    borderWidthTypeHover,
    borderWidthTypeNormal,
    titleFontSizeLarge,
    titleFontSizeSmall,
    titleFontSizeMedium,
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    showAuthor,
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    showDate,
    aspectRatioLarge,
    aspectRatioMedium,
    aspectRatioSmall,
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    authorColor,
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    authorFontSizeLarge,
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    authorFontSizeMedium,
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    authorFontSizeSmall,
    titleAlignment,
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    dateColor,
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    dateFontSizeLarge,
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    dateFontSizeMedium,
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    dateFontSizeSmall,
    horizontalGapLarge,
    horizontalGapMedium,
    horizontalGapSmall,
    overlayColor,
    verticalGapLarge,
    verticalGapMedium,
    verticalGapSmall,
    wrapperGapLarge,
    wrapperGapMedium,
    wrapperGapSmall,
    titleSemanticTag,
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    dateSemanticTag,
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    authorSemanticTag,
  } = useTheme<OnTopCardComponentThemeProps>(componentConfigThemeKey);
  const cardStyles = createCardStyles(cardTitleColorTypeNormal, cardTitleColorTypeHover, cardTitleColorTypeActive, borderRadius);
  const borderBoxShadow = {
    borderColorTypeActive,
    borderColorTypeHover,
    borderColorTypeNormal,
    borderWidthTypeActive,
    borderWidthTypeHover,
    borderWidthTypeNormal,
  };
  const boxShadowStyle = createStyleForBoxShadow({ boxShadowTypeNormal, boxShadowTypeHover, boxShadowTypeActive, borderBoxShadow });
  const titleAlignmentStyle = createTitleAlignmentStyle(titleAlignment);
  return (
    <WrapperForCard
      boxShadowStyle={boxShadowStyle}
      borderRadius={borderRadius}
      paddingLarge={wrapperGapLarge}
      paddingMedium={wrapperGapMedium}
      paddingSmall={wrapperGapSmall}
    >
      <Link className={css(cardStyles.card, boxShadowStyle.style)} href={articleUrl}>
        <Image {...image} lazyLoad={lazyLoad} imageAspectRatios={createImageAspectRatios(aspectRatioSmall, aspectRatioMedium, aspectRatioLarge)} imageHeightCalculationMethods={imageHeightCalculationMethods} imageWidths={imageWidths} />
        <CardOverlay overlayColor={overlayColor} />
        <CardCaptionContainer
          numberOfLines={numberOfLines}
          type={CARD_CAPTION_CONTAINER_TYPES.ON_TOP}
          horizontalMarginLarge={horizontalGapLarge}
          horizontalMarginMedium={horizontalGapMedium}
          horizontalMarginSmall={horizontalGapSmall}
          verticalMarginLarge={verticalGapLarge}
          verticalMarginMedium={verticalGapMedium}
          verticalMarginSmall={verticalGapSmall}
        >
          <DynamicSemanticTag semanticTag={titleSemanticTag} fontSizeLarge={titleFontSizeLarge} fontSizeMedium={titleFontSizeMedium} fontSizeSmall={titleFontSizeSmall} styles={semanticCSS(titleAlignmentStyle)}>{title}</DynamicSemanticTag>
        </CardCaptionContainer>
        {coverType === 'video' ? <PlayIcon /> : null}
      </Link>
    </WrapperForCard>
  );
};
